import * as React from 'react';
import { DayPicker, useNavigation } from 'react-day-picker';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FieldBox } from './FieldBox';
import { FieldSheet } from './FieldSheet';
import { cn } from '@/lib/utils';

// 'YYYY-MM-DD' -> local Date (midnight) without timezone drift.
function ymdToDate(ymd) {
  if (!ymd) return undefined;
  const [y, m, d] = String(ymd).split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}
function dateToYmd(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function ymdDisplay(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-');
  return `${d}.${m}.${y}`;
}

// Custom caption: month + year quick-jump dropdowns (Skyscanner-style).
function JumpCaption({ displayMonth, locale, fromYear, toYear }) {
  const { goToMonth } = useNavigation();
  const monthNames = React.useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { month: 'long' });
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2021, i, 1)));
  }, [locale]);
  const years = React.useMemo(() => {
    const out = [];
    for (let y = fromYear; y <= toYear; y += 1) out.push(y);
    return out;
  }, [fromYear, toYear]);

  const selCls =
    'appearance-none bg-slate-100 hover:bg-slate-200 rounded-lg pl-3 pr-7 py-1.5 text-sm font-semibold text-slate-900 transition-colors cursor-pointer outline-none focus:ring-2 focus:ring-[#FF6B35]/30';

  return (
    <div className="flex items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2">
        <div className="relative">
          <select
            className={selCls}
            value={displayMonth.getMonth()}
            onChange={(e) => goToMonth(new Date(displayMonth.getFullYear(), Number(e.target.value), 1))}
            data-testid="datefield-month-select"
          >
            {monthNames.map((mn, i) => (
              <option key={i} value={i}>{mn}</option>
            ))}
          </select>
          <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rotate-90 h-3.5 w-3.5 text-slate-500" />
        </div>
        <div className="relative">
          <select
            className={selCls}
            value={displayMonth.getFullYear()}
            onChange={(e) => goToMonth(new Date(Number(e.target.value), displayMonth.getMonth(), 1))}
            data-testid="datefield-year-select"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rotate-90 h-3.5 w-3.5 text-slate-500" />
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => goToMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1))}
          className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => goToMonth(new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 1))}
          className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * S-8 DateField — FieldBox that opens a calendar popover (desktop) / bottom sheet (mobile).
 * Value is a wall-date string 'YYYY-MM-DD'. Displays DD.MM.YYYY.
 */
export function DateField({
  label,
  value,
  onChange,
  min,
  max,
  placeholder,
  disabled = false,
  error = false,
  size = 'lg',
  testId,
  fromYear,
  toYear,
  className,
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const locale = i18n.language && i18n.language.startsWith('ru') ? 'ru-RU' : 'en-GB';
  const selected = ymdToDate(value);
  const now = new Date();
  const fy = fromYear ?? now.getFullYear() - 1;
  const ty = toYear ?? now.getFullYear() + 5;

  const disabledMatchers = [];
  const minD = ymdToDate(min);
  const maxD = ymdToDate(max);
  if (minD) disabledMatchers.push({ before: minD });
  if (maxD) disabledMatchers.push({ after: maxD });

  const handleSelect = (d) => {
    if (!d) return;
    onChange?.(dateToYmd(d));
    setOpen(false);
  };

  const trigger = (
    <FieldBox
      label={label}
      value={ymdDisplay(value)}
      placeholder={placeholder ?? t('fields.select_date', 'Select date')}
      icon={CalendarIcon}
      active={open}
      error={error}
      disabled={disabled}
      size={size}
      testId={testId}
      className={className}
    />
  );

  return (
    <FieldSheet
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      title={label || t('fields.select_date', 'Select date')}
      desktopWidth="w-[340px]"
      testId={testId}
    >
      <DayPicker
        mode="single"
        defaultMonth={selected || (minD && minD > now ? minD : now)}
        selected={selected}
        onSelect={handleSelect}
        disabled={disabledMatchers.length ? disabledMatchers : undefined}
        showOutsideDays={false}
        weekStartsOn={1}
        formatters={{
          formatWeekdayName: (d) => new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d),
        }}
        className="p-0"
        classNames={{
          months: 'flex flex-col',
          month: 'space-y-2',
          caption: 'relative',
          nav: 'hidden',
          table: 'w-full border-collapse',
          head_row: 'flex',
          head_cell: 'text-slate-400 rounded-md w-10 md:w-9 font-medium text-xs',
          row: 'flex w-full mt-1',
          cell: 'relative w-10 h-10 md:w-9 md:h-9 p-0 text-center',
          day: cn(
            'h-10 w-10 md:h-9 md:w-9 p-0 rounded-full text-sm font-medium text-slate-700',
            'hover:bg-slate-100 transition-colors cursor-pointer flex items-center justify-center mx-auto'
          ),
          day_selected:
            'bg-[#FF6B35] text-white hover:bg-[#FF6347] hover:text-white font-bold shadow-md shadow-[#FF6B35]/20',
          day_today: 'text-[#FF6B35] font-bold bg-[#FFE4D6]',
          day_outside: 'text-slate-300',
          day_disabled: 'text-slate-300 opacity-50 cursor-not-allowed hover:bg-transparent',
        }}
        components={{
          Caption: (p) => <JumpCaption {...p} locale={locale} fromYear={fy} toYear={ty} />,
        }}
      />
    </FieldSheet>
  );
}

export default DateField;
